import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import posed, { PoseGroup } from 'react-pose';
import { Translate } from 'react-localize-redux';
import css from './LinkEditorShelf.css';
import {
    DropdownField,
    Toggle,
    Slider,
    Button,
    WizardFooter,
    WarningMessage,
    Checkbox,
    QuestionMark,
    Label,
    QRCodeWidget,
    CopyAreaWithQRCode,
    Spinner,
    AutoCompleteField,
    TextField,
} from '../../components/widgets';
import WizardWarningMessage from '../../teamManagement/components/WizardWarningMessage';
import ShelfGroup from '../../components/widgets/ShelfGroup';
import Legacy from '../containers/Legacy';
import CopyArea from '../containers/CopyAreaCont';
import OGTagsPreview from '../../components/widgets/OGTagsPreview';
import OtherIcon from '../../resources/svg/icon_mobile.svg';
import Line from '../../resources/svg/line.svg';
import LinkParams from './LinkParams';
import { OSIcons } from '../../utils/OS';

const AnimationItem = posed.div({
    enter: {
        opacity: 1,
        y: 0,
        delay: ({ i }) => {
            return i * 50;
        },
    },
    exit: {
        opacity: 0,
        y: 20,
        transition: {
            y: {
                duration: 150,
            },
            opacity: {
                duration: 50,
            },
        },
    },
});

const getIconElement = os => {
    const IconElement = OSIcons[os] || OtherIcon;
    return <IconElement className={css.checkboxIcon} />;
};

const Separator = () => {
    return <div className={css.separator} />;
};

const RegularGroup = ({ children }) => {
    return <div>{children}</div>;
};

const CheckboxWithLabel = ({
    icon,
    label,
    hideCheckbox = false,
    hideIcon = false,
    checked = false,
    disabled = false,
    onChange = () => {},
    labelTooltip = null,
    labelTooltipStyle = {},
}) => {
    return (
        <div className={`${css.checkboxWithLabel} ${disabled && !hideCheckbox && !checked ? css.disabled : ''}`}>
            {!hideCheckbox ? (
                <Checkbox
                    size="medium"
                    className={css.checkbox}
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                />
            ) : null}
            {!hideIcon && getIconElement(icon)}
            <span className={`${css.labelText} ${hideIcon ? css.withSpacing : ''}`}>
                <Translate id={label} />
            </span>
            {labelTooltip ? (
                <div className={css.checkboxTooltip}>
                    <QuestionMark message={labelTooltip} interactive contentStyle={labelTooltipStyle} />
                </div>
            ) : null}
        </div>
    );
};

const LineLoader = () => {
    return (
        <div className={css.lineLoaderContainer}>
            <Line />
        </div>
    );
};

const LabelWithImage = ({ label, imageUrl, disabled = false }) => {
    return (
        <div className={`${css.labelWithImage} ${disabled ? css.disabled : ''}`}>
            {label && (
                <div className={css.labelWithImageLabel}>
                    <Translate id={label} />
                </div>
            )}
            {imageUrl && (
                <div className={css.labelWithImageContainer}>
                    <img src={imageUrl} alt="Preview" className={css.labelWithImageImg} />
                </div>
            )}
        </div>
    );
};

const ToggleExtended = ({ infoMessage, labelTooltipStyle = {}, ...props }) => {
    return (
        <div className={css.toggleExtendedContainer}>
            <Toggle {...props} style={{ width: 'auto' }} />
            {infoMessage ? (
                <div className={css.toggleExtendedInfo}>
                    <QuestionMark message={infoMessage} interactive contentStyle={labelTooltipStyle} />
                </div>
            ) : null}
        </div>
    );
};

const fieldsMap = {
    DropdownField: {
        FieldClass: DropdownField,
        onChangeFunc: ['onChange'],
        valueProp: 'selected',
        onBlurFunc: 'onBlur',
    },
    AutoCompleteField: {
        FieldClass: AutoCompleteField,
        onChangeFunc: ['onInputChange', 'onChange'],
        valueProp: 'value',
        onBlurFunc: 'onBlur',
    },
    Separator: {
        FieldClass: Separator,
        onChangeFunc: [],
    },
    CheckboxWithLabel: {
        FieldClass: CheckboxWithLabel,
        onChangeFunc: ['onChange'],
        valueProp: 'checked',
    },
    Toggle: {
        FieldClass: ToggleExtended,
        onChangeFunc: ['onToggle'],
        valueProp: 'checked',
    },
    Slider: {
        FieldClass: Slider,
        onChangeFunc: ['onChange'],
        valueProp: 'value',
    },
    CopyArea: {
        FieldClass: CopyArea,
        onChangeFunc: [],
        valueProp: 'content',
    },
    CopyAreaWithQRCode: {
        FieldClass: CopyAreaWithQRCode,
        onChangeFunc: [],
        valueProp: 'content',
    },
    QRCodeWidget: {
        FieldClass: QRCodeWidget,
        onChangeFunc: [],
        valueProp: 'value',
    },
    WarningMessage: {
        FieldClass: WarningMessage,
        onChangeFunc: [],
        valueProp: '',
    },
    Legacy: {
        FieldClass: Legacy,
        onChangeFunc: [],
        valueProp: '',
    },
    LineLoader: {
        FieldClass: LineLoader,
        onChangeFunc: [],
        valueProp: '',
    },
    LinkParams: {
        FieldClass: LinkParams,
        onChangeFunc: ['onUpdate'],
        valueProp: 'rows',
    },
    Label: {
        FieldClass: Label,
        onChangeFunc: [],
        valueProp: '',
    },
    OGTagsTextField: {
        FieldClass: TextField,
        onChangeFunc: ['onChange'],
        valueProp: 'value',
        onBlurFunc: 'onBlur',
    },
    OGTagsPreview: {
        FieldClass: OGTagsPreview,
        onChangeFunc: [],
        valueProp: '',
        valueProps: ['ogTitle', 'ogDescription', 'ogImageUrl', 'subDomain'],
    },
    LabelWithImage: {
        FieldClass: LabelWithImage,
        onChangeFunc: [],
        valueProp: '',
    },
};

export const DisplayFormikState = props => (
    <div style={{ margin: '1rem 0' }}>
        <h3 style={{ fontFamily: 'monospace' }} />
        <pre
            style={{
                background: '#f6f8fa',
                fontSize: '16px',
                padding: '10px',
            }}
        >
            <strong>props</strong> = {JSON.stringify(props, null, 2)}
        </pre>
    </div>
);

const DEFAULT_CATEGORIES = [];
const DEFAULT_FROM_DATA = {};
const DEFAULT_FUNC = () => {};

function LinkEditorShelf({
    validationSchema,
    shelf,
    fieldValues,
    onFormDirtyChanged = DEFAULT_FUNC,
    categories = DEFAULT_CATEGORIES,
    fetchFormData = DEFAULT_FROM_DATA,
    onGroupClicked = DEFAULT_FUNC,
    onFormUpdate = DEFAULT_FUNC,
    onCloseShelf = DEFAULT_FUNC,
    ...props
}) {
    const warningAction = props[shelf.errorButtonAction] ? props[shelf.errorButtonAction] : () => {};

    if (shelf.loading) {
        return <Spinner show expanded />;
    }

    return (
        <div className={css.container}>
            <WizardWarningMessage
                show={!!shelf.error}
                showIcon={false}
                message={shelf.error}
                type={shelf.errorType}
                buttonAction={() => {
                    warningAction(fieldValues);
                }}
                buttonText={shelf.errorButtonText}
            />
            {!fieldValues.linkType && shelf.error ? null : ( // don't display form when error accord on get link
                <div className={css.contentContainer}>
                    <Label type="shelfHeader" text="STATIC.PAGES.MANAGE_LINKS.SHELF_TITLE" />
                    <Formik
                        onSubmit={(values, actions) => {
                            setTimeout(() => {
                                alert(JSON.stringify(values, null, 2));
                                actions.setSubmitting(false);
                            }, 1000);
                        }}
                        initialValues={fieldValues}
                        validationSchema={validationSchema}
                        render={formikProps => (
                            <MyForm
                                onFormDirtyChanged={onFormDirtyChanged}
                                fieldValues={fieldValues}
                                shelf={shelf}
                                {...formikProps}
                                categories={categories}
                                onCloseShelf={onCloseShelf}
                                onGroupClicked={onGroupClicked}
                                fetchFormData={fetchFormData}
                                onFormUpdate={onFormUpdate}
                                {...props}
                            />
                        )}
                    />
                </div>
            )}
        </div>
    );
}

function MyForm(props) {
    const {
        values,
        touched,
        dirty,
        errors,
        handleSubmit,
        setValues,
        setTouched: setTouchedProp,
        categories,
        onCloseShelf,
        onGenerate,
        onUpdate,
        onFormDirtyChanged,
        onGroupClicked: onGroupClickedProp,
        shelf,
        fieldValues,
        handleReset,
    } = props;

    const dirtyFieldsRef = useRef(new Set());
    const categoryRefs = useRef({});
    const fieldRefs = useRef({});
    const currentPropsRef = useRef();
    const prevPropsRef = useRef();

    currentPropsRef.current = props;

    const getAllResetFields = useCallback(categories => {
        return categories.reduce((total, category) => {
            total.push(...category.fields.filter(f => f.resetField).map(f => f.name));
            return total;
        }, []);
    }, []);

    const getAllFields = useCallback(categories => {
        return categories.reduce((total, category) => {
            total.push(...category.fields.map(f => f.name));
            return total;
        }, []);
    }, []);

    const getAllPropsFields = useCallback(() => {
        return getAllFields(categories);
    }, [categories, getAllFields]);

    const isGroupDisabled = useCallback(
        (group, values) => {
            if (!group.disabledRules) {
                return group.disabled;
            }
            return group.disabledRules.reduce((t, rule) => {
                return t || rule(values, categories);
            }, false);
        },
        [categories]
    );

    const getErrorFormatted = useCallback(error => {
        if (typeof error === 'object') {
            return Object.values(error).pop();
        }
        return error;
    }, []);

    const onGroupClicked = useCallback(
        groupName => {
            const currentCategory = categories.find(cat => cat.name === groupName);
            if (currentCategory.collapsed) {
                setTimeout(() => {
                    const categoryElement = categoryRefs.current[`category_${groupName}`];
                    if (categoryElement) {
                        categoryElement.scrollIntoView();
                    }
                }, 150);
            }
            onGroupClickedProp(groupName, values);
        },
        [onGroupClickedProp, values, categories]
    );

    const setTouched = useCallback(
        fieldName => {
            // Set all previous fields as touched.
            // This is for cases where fields are invalid and were skipped by the user.
            if (touched[fieldName]) {
                return;
            }
            const allFields = getAllPropsFields();
            const fieldsTouched = allFields
                .slice(0, allFields.findIndex(fName => fName === fieldName) + 1)
                .reduce((total, fName) => {
                    total[fName] = true;
                    return total;
                }, {});
            setTouchedProp(fieldsTouched);
        },
        [setTouchedProp, categories, touched, getAllPropsFields]
    );

    const createGenericChangeFunc = field => value => {
        // Get the absolute latest props from the ref to avoid stale closures
        const latestProps = currentPropsRef.current;
        const currentValues = latestProps.values;
        const currentDirty = latestProps.dirty;
        const currentOnFormUpdate = latestProps.onFormUpdate;
        const currentSetFieldValue = latestProps.setFieldValue;

        const oldValue = currentValues[field.name];
        let useValue = value;
        if (useValue === null) {
            useValue = '';
        }
        if (typeof useValue === 'object' && 'target' in useValue) {
            useValue = useValue.target.checked;
        }

        currentSetFieldValue(field.name, useValue);
        setTouched(field.name);
        field.prevValue = oldValue;
        if (field.updateOnChange) {
            currentOnFormUpdate(
                field,
                {
                    ...currentValues,
                    [field.name]: useValue,
                },
                false,
                currentDirty
            );
        } else {
            dirtyFieldsRef.current.add(field.name);
        }
    };

    useEffect(() => {
        const prevProps = prevPropsRef.current;
        if (!prevProps) {
            prevPropsRef.current = props;
            return;
        }

        if (prevProps.fieldValues !== fieldValues) {
            const resetFields = getAllResetFields(categories);
            const resetFieldChange = resetFields.some(fieldName => {
                const prevValue = prevProps.fieldValues?.[fieldName] ?? null;
                const currentValue = fieldValues[fieldName] ?? null;
                return prevValue !== currentValue;
            });

            if (resetFieldChange) {
                dirtyFieldsRef.current.clear();
            }

            const newValues = Object.entries(fieldValues).reduce((total, [key, value]) => {
                total[key] = value;
                if (dirtyFieldsRef.current.has(key)) {
                    total[key] = currentPropsRef.current.values[key];
                }
                return total;
            }, {});

            setValues(newValues);
            dirtyFieldsRef.current.clear();
        }

        if (prevProps.categories !== categories) {
            const allPropsFields = getAllFields(prevProps.categories);
            const allNextPropsFields = getAllFields(categories);
            if (allPropsFields.length !== allNextPropsFields.length) {
                setTimeout(() => {
                    const lastElementName = allNextPropsFields[allNextPropsFields.length - 1];
                    const lastElement = fieldRefs.current[`field_${lastElementName}`];
                    if (lastElement) {
                        lastElement.scrollIntoView();
                    }
                }, 50);
            }
        }

        if (prevProps.shelf.saved && !shelf.saved) {
            handleReset();
        }

        if (prevProps.dirty !== dirty) {
            onFormDirtyChanged(dirty);
        }

        prevPropsRef.current = props;
    }, [
        fieldValues,
        categories,
        shelf.saved,
        dirty,
        getAllResetFields,
        getAllFields,
        setValues,
        handleReset,
        onFormDirtyChanged,
    ]);

    const isDuplicationMode = shelf.headerText === 'Duplicate Link';
    const hasErrors = !(dirty || isDuplicationMode) || !!Object.keys(errors).length;

    const buttonDefs = {
        cancel: {
            text: <Translate id="STATIC.BUTTONS.CANCEL" />,
            disabled: false,
            type: 'flat',
            onClick: onCloseShelf,
        },
        generate: {
            text: <Translate id="STATIC.PAGES.MANAGE_LINKS.GENERATE_BUTTON" />,
            type: 'primary',
            onClick: () => {
                onGenerate(values);
            },
            disabled: !shelf.working && hasErrors,
        },
        done: {
            text: <Translate id="STATIC.BUTTONS.DONE" />,
            type: 'primary',
            onClick: onCloseShelf,
            onDirty: 'update',
            disabled: false,
        },
        update: {
            text: <Translate id="STATIC.PAGES.MANAGE_LINKS.UPDATE_BUTTON" />,
            type: 'primary',
            onClick: () => {
                onUpdate(values);
            },
            disabled: !shelf.working && hasErrors,
        },
    };

    let actionButton = buttonDefs[shelf.actionButton];
    if (dirty && actionButton.onDirty && !shelf.readOnly) {
        actionButton = buttonDefs[actionButton.onDirty];
    }

    return (
        <form onSubmit={handleSubmit}>
            {categories.map(category => {
                const GroupClass = category.collapseable ? ShelfGroup : RegularGroup;
                const groupDisabled = isGroupDisabled(category, values);
                return (
                    <div
                        className={css.groupItemContainer}
                        key={category.name}
                        ref={el => {
                            categoryRefs.current[`category_${category.name}`] = el;
                        }}
                    >
                        {category.isLoading ? (
                            <Spinner show className={css.groupItemSpinner} />
                        ) : (
                            <GroupClass {...category} disabled={groupDisabled} onGroupClicked={onGroupClicked}>
                                <PoseGroup animateOnMount>
                                    {category.fields.map(field => {
                                        const genericChangeFunc = createGenericChangeFunc(field);
                                        const {
                                            FieldClass,
                                            onChangeFunc,
                                            onBlurFunc,
                                            valueProp,
                                            valueProps,
                                        } = fieldsMap[field.component];
                                        const fieldProps = field.componentData;
                                        onChangeFunc.forEach(func => {
                                            fieldProps[func] = genericChangeFunc;
                                        });
                                        fieldProps[onBlurFunc] = () => {
                                            setTouched(field.name);
                                        };
                                        fieldProps[valueProp] = values[field.name];
                                        if (valueProps && Array.isArray(valueProps)) {
                                            valueProps.forEach(propName => {
                                                fieldProps[propName] = values[propName] || '';
                                            });
                                        }
                                        return (
                                            <AnimationItem key={field.name}>
                                                <div
                                                    ref={el => {
                                                        fieldRefs.current[`field_${field.name}`] = el;
                                                    }}
                                                >
                                                    <FieldClass
                                                        {...fieldProps}
                                                        error={
                                                            (touched[field.name] || values.linkId) &&
                                                            getErrorFormatted(errors[field.name])
                                                        }
                                                    />
                                                </div>
                                            </AnimationItem>
                                        );
                                    })}
                                </PoseGroup>
                            </GroupClass>
                        )}
                    </div>
                );
            })}
            {/* <DisplayFormikState {...{ touched, dirty, errors, values }} />*/}
            <WizardFooter
                buttons={[
                    <Button {...(({ text, ...rest }) => rest)(buttonDefs.cancel)}>{buttonDefs.cancel.text}</Button>,
                    <Button
                        {...(({ text, ...rest }) => rest)(actionButton)}
                        showSpinner={(shelf.working && !shelf.saved) || categories.some(x => x.isLoading)}
                        showV={shelf.saved && !shelf.working}
                    >
                        {actionButton.text}
                    </Button>,
                ]}
            />
        </form>
    );
}

LinkEditorShelf.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.object),
    onGroupClicked: PropTypes.func,
    onFormUpdate: PropTypes.func,
    onCloseShelf: PropTypes.func,
    validationSchema: PropTypes.any,
    shelf: PropTypes.any,
    fieldValues: PropTypes.any,
    fetchFormData: PropTypes.any,
    onFormDirtyChanged: PropTypes.func,
};

export default LinkEditorShelf;
